import { useState, useCallback, useEffect } from "react";
import { Wallet } from "../services/wallet";

import { ChatWindow } from "./ChatWindow";
import { MessageInput } from "./MessageInput";
// import { useChatConnection } from "../hooks/useChatConnection";
import { Box, useMediaQuery, useTheme } from "@mui/material";

import ChatList from "./ChatList";
import ChatGroup from "./ChatGroup";
import { Contact } from "../models/Contact";
import ChatHeader from "./ChatHeader";
import { onMessage } from "firebase/messaging";
import { useAppSelector, useAppDispatch } from "../hooks/hooks";
import {
  fetchMessages,
  sendNewMessage,
  clearMessageParts,
  fetchMessagesByContact,
} from "../redux/messagesSlice";
import { fetchMessageContacts, selectContact } from "../redux/chatListSlice";

import { fetchGiftBrands } from "../redux/giftBrandsSlice";
import { fetchNfts, openNftSelectionDrawer } from "../redux/nftSlice";
import { GiftBrand } from "../models/GiftBrand";
import { getFirebaseMessaging } from "../firebase/firebase";
import { logout } from "../redux/authSlice";
import { getReceiversPublicKey } from "../redux/userSlice";
import BuyGiftDialog from "./BuyGiftDialog";
import {
  addGiftBrand,
  openBuyGiftsDrawer,
  closeBuyGiftsDrawer,
} from "../redux/giftShoppingCartSlice";
import ProfileDialog from "./ProfileDialog";
import { AuthForm } from "./AuthForm";
import { setWalletDialogOpen } from "../redux/walletSlice";

import WalletDialog from "./WalletDialog";
import {
  closeProfileDialog,
  setSelectedSettingsTab,
  setCurrentNavigationView,
  setIsCompletingLogin,
} from "../redux/uiSlice";
import { composeMessage, MessagePart } from "../utils/messageUtils";
import { setSelectedChatView } from "../redux/uiSlice";
import { getCountries } from "../redux/countriesSlice";
import { formatLastSeen } from "../utils/formatLastSeen";
import {
  useConnection,
  useChatConnection,
} from "../contexts/ConnectionContext";
import { useSecurity } from "../contexts/SecurityContext";
import { SplashScreen } from "./SplashScreen";

const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

export default function Chat() {
  const theme = useTheme();
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  // State management
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const { selectedContact } = useAppSelector((state) => state.chatList);
  const dispatch = useAppDispatch();
  // const { token } = useAppSelector((state) => state.auth);
  const { contacts } = useAppSelector((state) => state.contacts);

  const { giftBrands } = useAppSelector((state) => state.giftBrands);

  const { walletDialogOpen } = useAppSelector((state) => state.wallet);
  const messaging = getFirebaseMessaging();
  const { messageParts } = useAppSelector((state) => state.messages);
  const { selectedChatView, profileDialogOpen, isCompletingLogin } =
    useAppSelector((state) => state.ui);
  const { receiversPublicKey } = useAppSelector((state) => state.user);

  const { buyGiftsDrawerOpen } = useAppSelector((state) => state.shoppingCart);

  /// Initialize connection
  const { connection, status, startConnection, stopConnection } =
    useConnection();
  const { handleIncomingMessage, handleUserStatusChanged } =
    useChatConnection();
  const { token } = useAppSelector((state) => state.auth);
  const { privateKey, publicKey } = useSecurity();

  useEffect(() => {
    if (token !== undefined) {
      dispatch(setIsCompletingLogin(false));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (!token && status === "connected") {
      stopConnection();
      window.location.reload();
    }
    if (token) {
      startConnection(token, handleIncomingMessage, handleUserStatusChanged);
    } else {
      // stop connection when token is null
      if (connection && connection.state !== "Disconnected") {
        stopConnection();
      }
    }
  }, [
    token,
    status,
    startConnection,
    handleIncomingMessage,
    handleUserStatusChanged,
    connection,
    stopConnection,
  ]);
  ///

  useEffect(() => {
    if (selectedChatView === "list") {
      dispatch(fetchMessageContacts());
    }
  }, [dispatch, selectedChatView]);

  useEffect(() => {
    const fetchMessagesForSelection = async (contact: Contact) => {
      contact.isGroup
        ? dispatch(setSelectedChatView("group"))
        : dispatch(setSelectedChatView("chat"));

      await dispatch(
        getReceiversPublicKey({
          receiverId: contact.walletAddress || "",
        })
      );

      await dispatch(
        fetchMessagesByContact({
          contact: contact.walletAddress || "",
        })
      );
    };

    if (selectedContact) {
      fetchMessagesForSelection(selectedContact);
    }
  }, [dispatch, selectedContact]);

  const handleNewContact = () => {
    dispatch(setCurrentNavigationView("settings"));
    dispatch(setSelectedSettingsTab("contacts"));
  };

  const handleNewGroup = () => {
    dispatch(setCurrentNavigationView("settings"));
    dispatch(setSelectedSettingsTab("contacts"));
  };

  const handleSend = useCallback(async () => {
    if (!connection || !messageInput.trim()) return;

    const newMessageParts: MessagePart[] = [
      ...messageParts,
      { type: "text", content: messageInput.trim() },
    ];

    try {
      await dispatch(
        sendNewMessage({
          content: composeMessage(newMessageParts),
          connection,
          receiversPublicKey,
          privateKey,
          publicKey,
          selectedContact,
          messageParts: newMessageParts,
          walletAddress: wallet?.account ?? "",
        })
      ).unwrap();

      setMessageInput("");
      dispatch(clearMessageParts());
    } catch (error) {
      console.error("Message send failed:", error);
    }
  }, [
    connection,
    messageInput,
    messageParts,
    dispatch,
    receiversPublicKey,
    privateKey,
    publicKey,
    selectedContact,
    wallet?.account,
  ]);

  const handleAttachGift = async () => {
    await dispatch(fetchGiftBrands({ countryCode: "CA", currentPage: 1 }));
    dispatch(openBuyGiftsDrawer());
  };

  const handleAttachNFT = async () => {
    await dispatch(fetchNfts());
    dispatch(openNftSelectionDrawer());
  };

  const handleBuyGift = async () => {
    await dispatch(fetchGiftBrands({ countryCode: "CA", currentPage: 1 }));
    dispatch(openBuyGiftsDrawer());
  };

  // Handle incoming messages
  const setupMessageHandler = useCallback(
    (callback: (payload: any) => void) => {
      if (messaging) {
        return onMessage(messaging, (payload) => {
          callback(payload);
        });
      } else {
        // Return a no-op unsubscribe function if messaging is null
        return () => {};
      }
    },
    [messaging]
  );

  useEffect(() => {
    const unsubscribe = setupMessageHandler((payload) => {
      if (payload.data?.type === "new_message") {
        dispatch(fetchMessages(payload.data.groupId));
      }
    });

    dispatch(getCountries());

    return () => unsubscribe();
  }, [dispatch, messaging, setupMessageHandler]);

  const handleLogout = useCallback(async () => {
    try {
      // Stop the connection first
      if (connection) {
        //await connection.stop();
        stopConnection();
      }

      // Then dispatch logout
      dispatch(logout());

      // Reset wallet state
      setWallet(null);

      // Close profile dialog

      dispatch(closeProfileDialog());
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, [connection, dispatch, stopConnection]);

  const renderContent = () => {
    // if (token === undefined) {
    //   return <div>Loading...</div>;
    // }

    if (isCompletingLogin) {
      return <SplashScreen />;
    }

    if (token === undefined) {
      return <SplashScreen />;
    }

    if (!token) {
      return (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",

            padding: 2,
            boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <AuthForm />
        </Box>
      );
    }

    return (
      <>
        {selectedChatView === "list" ? (
          <Box
            sx={{
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ChatList
              contacts={contacts}
              onSelect={async function (contact: Contact): Promise<void> {
                await dispatch(selectContact({ contact }));
              }}
              onNewContact={handleNewContact}
              onNewGroup={handleNewGroup}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ChatHeader
              title={selectedContact?.name ?? ""}
              onBack={function (): void {
                dispatch(setSelectedChatView("list"));
              }}
              onlineStatus={
                selectedContact?.status === "online"
                  ? "Online"
                  : formatLastSeen(selectedContact?.lastSeen ?? "")
              }
            />
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {selectedChatView === "chat" ? <ChatWindow /> : <ChatGroup />}
            </Box>

            <MessageInput
              message={messageInput}
              onMessageChange={setMessageInput}
              onSend={handleSend}
              onAttachGift={handleAttachGift}
              onAttachNFT={handleAttachNFT}
              onBuyGift={handleBuyGift}
            />
          </Box>
        )}

        {/* <NewContactDialog
          open={showNewContact}
          onClose={() => setShowNewContact(false)}
        /> */}
        {/* <NewGroupDialog
          open={showNewGroup}
          onClose={() => setShowNewGroup(false)}
        /> */}
        <BuyGiftDialog
          brands={giftBrands}
          open={buyGiftsDrawerOpen}
          onClose={() => dispatch(closeBuyGiftsDrawer())}
          onSelect={async (brand: GiftBrand) => {
            dispatch(closeBuyGiftsDrawer());
            await dispatch(addGiftBrand(brand));
          }}
        />
        {/* <SelectGiftsDialog
          open={nftSelectionDrawerOpen}
          onClose={() => dispatch(closeNftSelectionDrawer())}
          brands={giftBrands}
          onSelect={(brand: GiftBrand) => {
            dispatch(closeNftSelectionDrawer());
          }}
        /> */}

        <ProfileDialog
          open={profileDialogOpen}
          onClose={() => {
            dispatch(closeProfileDialog());
          }}
          onLogout={handleLogout}
        />
        <WalletDialog
          open={walletDialogOpen}
          onClose={() => {
            dispatch(setWalletDialogOpen(false));
          }}
          onDisconnect={() => {}}
        />

        {/* <ShoppingCart /> */}
        {/* <CheckoutForm /> */}
      </>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        paddingBottom: { xs: 7, sm: 8 },
        paddingRight: { xs: "10px", sm: "12px" },
        minHeight: isMobileView
          ? "calc(100dvh - 320px)"
          : "calc(100dvh - 300px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        // Mobile-specific styles
        ...(isMobileView && {
          maxWidth: "100%",
          borderRadius: 0,
        }),
        // Desktop-specific styles
        ...(!isMobileView && {
          boxShadow: "0 0 20px rgba(0,0,0,0.1)",
          borderLeft: "1px solid rgba(0,0,0,0.1)",
          borderRight: "1px solid rgba(0,0,0,0.1)",
        }),
      }}
    >
      {renderContent()}
      {/* Only show AppNavigation when authenticated */}
      {/* {token && <AppNavigation />} */}
    </Box>
  );
}
